import type { BankImportResultDto } from "@boo/shared";
import { request } from "./apiClient";

export interface GmailConnectionStatus {
  connected: boolean;
  email?: string;
  connectedAt?: string;
}

export interface BankConfig {
  id: string;
  label: string;
  senderPattern: string;
  hasPassword: boolean;
  accountLast4: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface CreateBankConfigPayload {
  label: string;
  senderPattern: string;
  pdfPassword: string;
  accountNumber?: string;
}

export interface UpdateBankConfigPayload {
  label?: string;
  senderPattern?: string;
  pdfPassword?: string;
  accountNumber?: string;
  isActive?: boolean;
}

export async function getGmailConnectionStatus(): Promise<GmailConnectionStatus> {
  return request<GmailConnectionStatus>("/gmail-integration/status");
}

export async function getGmailConnectUrl(): Promise<{ url: string }> {
  return request<{ url: string }>("/gmail-integration/connect");
}

export async function disconnectGmail(): Promise<void> {
  await request<void>("/gmail-integration/disconnect", { method: "DELETE" });
}

export async function triggerGmailImport(): Promise<{ imported: number }> {
  return request<{ imported: number }>("/gmail-integration/import", {
    method: "POST",
  });
}

export const MAX_STATEMENT_BYTES = 20 * 1024 * 1024; // 20 MB

export async function uploadBankStatement(
  file: File,
): Promise<BankImportResultDto> {
  const formData = new FormData();
  formData.append("file", file);
  return request<BankImportResultDto>("/gmail-integration/upload", {
    method: "POST",
    data: formData,
  });
}

export async function fetchBankConfigs(): Promise<BankConfig[]> {
  return request<BankConfig[]>("/bank-configs");
}

export async function createBankConfig(
  data: CreateBankConfigPayload,
): Promise<BankConfig> {
  return request<BankConfig>("/bank-configs", { method: "POST", data });
}

export async function updateBankConfig(
  id: string,
  data: UpdateBankConfigPayload,
): Promise<BankConfig> {
  return request<BankConfig>(`/bank-configs/${id}`, { method: "PATCH", data });
}

export async function deleteBankConfig(id: string): Promise<void> {
  await request<void>(`/bank-configs/${id}`, { method: "DELETE" });
}
